export interface NavItem {
  id: string;
  icon: string;
  label: string;
  path: string;
  count?: number;
  hideForExternal?: boolean;
  // Also hide from Staff (role 4). With hideForExternal, this yields "Manager and above"
  // (roles 1-3) visibility. NOTE: the current ShellUser (fed by /me) exposes only
  // isExternal — no role/staff signal — so RailSidebar/mobileNav cannot yet filter on
  // this. Wire the filter once /me exposes role; until then this is a declarative marker
  // and the live gate for this surface is app.js pages[] (userRole <= 3).
  hideForStaff?: boolean;
  // Show only when this /me permission flag is true (keyof the me slice's `permissions`,
  // e.g. 'canViewAdminReports'). Unlike hideForStaff, this IS wired — filterNavItems reads
  // the flag off /me, so role decisions stay on the BE (permissions belong in BE) and the
  // FE never does role math. Prefer this over hideForStaff for new role-gated surfaces.
  requiresPermission?: string;
  // Non-route item: clicking runs an in-app action instead of navigating. Action items carry
  // `path: ''` so route matching (pathnameToTabId, activePath === item.path) can never resolve
  // to them. Menus must branch on this before calling onNavigate.
  action?: 'contact-support';
}

// Contact Support opens a modal (ContactSupportModal) rather than navigating — there is no
// '/contact-support' route in app.js pages[], which is why the earlier rail dropped the entry
// altogether. Exported so the client More menu (clientNav) reuses the same definition;
// support is not role-gated, matching the legacy sidebar which showed it to everyone.
export const CONTACT_SUPPORT_ITEM: NavItem = {
  id: 'support',
  icon: 'life-buoy',
  label: 'Contact Support',
  path: '',
  action: 'contact-support',
};

/**
 * Filters a nav list for the current user. Drops `hideForExternal` items for external
 * users, and `requiresPermission` items unless that /me permission flag is true. A missing
 * `permissions` map denies every gated item (default-deny until /me resolves).
 */
export const filterNavItems = (
  items: NavItem[],
  {
    isExternal,
    permissions,
  }: { isExternal: boolean; permissions?: Record<string, boolean> }
): NavItem[] =>
  items.filter(
    (m) =>
      !(m.hideForExternal && isExternal) &&
      (!m.requiresPermission || !!permissions?.[m.requiresPermission])
  );

// Primary rail items. Routes match the existing hash routes in app.js pages[].
export const NAV_ITEMS: NavItem[] = [
  { id: 'account', icon: 'building-2', label: 'Accounts', path: '/' },
  {
    id: 'tasks',
    icon: 'square-check-big',
    label: 'My Tasks',
    path: '/my-tasks',
  },
  {
    id: 'messages',
    icon: 'message-square',
    label: 'Messages',
    path: '/my-conversations',
  },
  { id: 'timekeep', icon: 'clock', label: 'Timekeep', path: '/my-timekeep' },
  {
    id: 'notifications',
    icon: 'bell',
    label: 'Notifs',
    path: '/notifications',
  },
];

// "More" flyout items.
export const MORE_ITEMS: NavItem[] = [
  {
    id: 'users',
    icon: 'users',
    label: 'Users',
    path: '/users',
    hideForExternal: true,
  },
  { id: 'reports', icon: 'bar-chart-3', label: 'Reports', path: '/reports' },
  // Admin Reports (parent-report definitions). ADMIN AND ABOVE (Master Admin / Admin): the
  // route in app.js pages[] is gated userRole < 3. Server-side the admin gate lives in the
  // service layer (AdminReportsPermissionService.get) — ReportDefinitionsController uses bare
  // @Authorize() (auth-only, any logged-in user). Gated here on the canViewAdminReports flag
  // from /me (which mirrors that same admin-or-above rule) so the link renders for admins
  // without the FE knowing role numbers.
  {
    id: 'admin-reports',
    icon: 'file-cog',
    label: 'Admin Reports',
    path: '/report-definitions',
    hideForExternal: true,
    requiresPermission: 'canViewAdminReports',
  },
  // Company-wide email log (crashed-mail triage). MANAGER AND ABOVE (roles 1-3): the route
  // in app.js pages[] is gated userRole <= 3, and the BE controller enforces
  // @Authorize([For.Manager]). hideForExternal alone is NOT enough here — it would still
  // show to Staff. See hideForStaff note on NavItem for the pending RailSidebar filter
  // (blocked on /me exposing role).
  {
    id: 'email-log',
    icon: 'mail',
    label: 'Email Log',
    path: '/email-log',
    hideForExternal: true,
    hideForStaff: true,
  },
  { id: 'settings', icon: 'settings', label: 'Settings', path: '/settings' },
  // Corrected from '/legal-center' — real path_name in app.js pages[] is 'legal-information-center'
  {
    id: 'legal',
    icon: 'scale',
    label: 'Legal Information Center',
    path: '/legal-information-center',
  },
  CONTACT_SUPPORT_ITEM,
];
